<template>
  <div v-if="datesLoaded" class="dates-selection">
    <v-card class="d-flex pa-2 pb-0 col-box no-border">
      <div>
        <h3 class="dates-selection__input-heading">
          {{ tkey('snapshotDateLabel') }}

          <!-- Snapshot date tooltip -->
          <AssortmentTooltip
            :title="$t('tooltips.snapshotDate.title')"
            :tooltip-sections="snapshotDateTooltipSections"
          />
        </h3>
        <div data-id-e2e="dateSnapshot" class="dates-selection__input">
          <RtlsCalendarPicker
            :key="nameKey"
            :value="wpDateSelections.snapshotDate"
            :allowed-dates="formattedSnapshotDates"
            :display-date-format="longDateFormat"
            @input="val => handleDateUpdate(val, 'snapshotDate')"
          />
        </div>
      </div>
      <div v-if="hasPeriodIdsEnabled" class="ml-8">
        <v-row class="ma-0">
          <h3 class="dates-selection__input-heading">
            {{ tkey('assortmentPeriodNumbers') }}
          </h3>
        </v-row>

        <v-row class="ma-0">
          <v-autocomplete
            class="rtls-select--multi-select"
            chips
            closable-chips
            multiple
            hide-details
            :model-value="wpModelSelections.periodIds || []"
            :items="parsedPeriodIdsOptions"
            item-title="text"
            item-value="value"
            :style="{ 'max-width': '280px', width: '20rem' }"
            :placeholder="$t('general.select')"
            :menu-props="{ dense: true, contentClass: 'rtls-multi-select-list' }"
            @update:model-value="val => updateModel(val, 'periodIds')"
          >
            <template #chip="{ item, index, props }">
              <v-chip v-if="index === 0" v-bind="props">
                <span>{{ item.title }}</span>
              </v-chip>
              <span v-if="index === 1" class="text-grey text-caption">
                (+{{ wpModelSelections.periodIds.length - 1 }})
              </span>
            </template>
          </v-autocomplete>

          <v-tooltip
            :disabled="!automaticDatesDisabledTooltip"
            :text="automaticDatesDisabledTooltip"
          >
            <template #activator="{ props }">
              <span v-bind="props">
                <v-btn
                  v-if="betaAutoGenerateWorkpackagePeriodDatesEnabled"
                  :disabled="!selectedPeriodHasSingleDateRange"
                  :loading="loadingAGsForPeriodId"
                  variant="flat"
                  primary
                  class="ml-3 autodate-btn"
                  @click="generateDatesFromPeriodId"
                >
                  {{ tkey('automaticDatesAndAgsBtn') }}
                </v-btn>
              </span>
            </template>
          </v-tooltip>
        </v-row>
      </div>
      <div
        v-if="bundlesEnabled && selectedWorkpackage.bundleId && selectedWorkpackage.templateId"
        class="ml-4"
      >
        <h3 class="dates-selection__input-heading">
          {{ $t('bundlesPage.goLive') }}

          <!-- Go live date tooltip -->
          <AssortmentTooltip
            :title="$t('tooltips.targetLaunchDate.title')"
            :tooltip-sections="targetLaunchDateTooltipSections"
          />
        </h3>
        <div data-id-e2e="targetLaunchDate" class="dates-selection__input">
          <RtlsCalendarPicker
            :key="nameKey"
            :value="wpDateSelections.targetLaunchDate"
            :display-date-format="longDateFormat"
            @input="val => handleDateUpdate(val, 'targetLaunchDate')"
          />
        </div>
      </div>
    </v-card>

    <v-card class="mt-2 mb-1 mx-2 col-box border-card">
      <!-- performance date -->
      <h3 class="mt-3 dates-selection__input-heading">
        {{ tkey('performancePeriodLabel') }}
        <span>{{ tkey('performancePeriodLabelWeeks') }}</span>

        <!-- Performance period tooltip -->
        <AssortmentTooltip
          :title="$t('tooltips.performancePeriod.title')"
          :tooltip-sections="performancePeriodTooltipSections"
        />
      </h3>
      <div class="dates-selection__input-container">
        <div
          data-id-e2e="datePerformance"
          class="dates-selection__input dates-selection__input--longer"
        >
          <span>{{ tkey('startLabel') }}:</span>
          <RtlsCalendarPicker
            :key="nameKey"
            :value="wpDateSelections.performanceStartDate"
            :allowed-dates="allowedStartAndExcludedDates"
            :display-date-format="longDateFormat"
            @input="val => handleDateUpdate(val, 'performanceStartDate')"
          />
        </div>
        <span class="mr-0">
          {{ tkey('endLabel') }}:
          <v-tooltip
            :model-value="endDatesExceeded.performance"
            :disabled="!endDatesExceeded.performance"
            location="right"
          >
            <template #activator="{ props }">
              <b v-bind="props">
                {{ formatDate(wpDateSelections.performanceEndDate, longDateFormat) }}
              </b>
            </template>
            <span v-if="endDatesExceeded.performance">{{ tkey('maxWeekWarning') }}</span>
          </v-tooltip>
        </span>
      </div>
      <!-- end performance date -->
      <WeekSelector
        v-model="wpDateSelections.performanceDateWeeks"
        data-id-e2e="btnPerformanceWeeks"
        :model-value="wpDateSelections.performanceDateWeeks"
        :week-options="defaultWeekOptions"
        :placeholder="tkey('custom')"
        @update:model-value="val => handleDateUpdate(val, 'performanceDateWeeks')"
      />
    </v-card>

    <!-- excluding date -->
    <v-card class="pa-2 col-box no-border">
      <div data-id-e2e="dateExclude" class="dates-selection__exclude-container">
        <h3 class="dates-selection__input-heading">{{ tkey('excludingLabel') }}:</h3>
        <MultipleDatesSelector
          :value="wpDateSelections.performanceExcludedDates"
          :allowed-dates="allowedStartAndExcludedDates"
          @input="val => handleDateUpdate(val, 'performanceExcludedDates')"
        />
      </div>
    </v-card>
    <!-- end excluding date -->

    <!-- learning period-->
    <template v-if="hasSeasonalityEnabled && !isSimpleSwapsWP">
      <v-card class="mt-2 mb-1 mx-2 col-box border-card">
        <!-- learning date -->
        <h3 class="mt-3 dates-selection__input-heading">
          {{ tkey('learningPeriodLabel') }}
          <span>{{ tkey('learningPeriodLabelWeeks') }}</span>

          <!-- Learning period tooltip -->
          <AssortmentTooltip
            :title="$t('tooltips.learningPeriod.title')"
            :tooltip-sections="learningPeriodTooltipSections"
          />
        </h3>
        <div class="dates-selection__input-container">
          <div
            data-id-e2e="dateLearning"
            class="dates-selection__input dates-selection__input--longer"
          >
            <span>{{ tkey('startLabel') }}:</span>
            <RtlsCalendarPicker
              :key="`learning-start-date-${nameKey}`"
              :value="wpDateSelections.learningStartDate"
              :allowed-dates="allowedStartAndExcludedDates"
              :display-date-format="longDateFormat"
              @input="val => handleDateUpdate(val, 'learningStartDate')"
            />
          </div>
          <span class="mr-0">
            {{ tkey('endLabel') }}:
            <v-tooltip
              :model-value="endDatesExceeded.learning"
              :disabled="!endDatesExceeded.learning"
              location="right"
            >
              <template #activator="{ props }">
                <b v-bind="props">
                  {{ formatDate(wpDateSelections.learningEndDate, longDateFormat) }}
                </b>
              </template>
              <span v-if="endDatesExceeded.learning">{{ tkey('maxWeekWarning') }}</span>
            </v-tooltip>
          </span>
        </div>
        <!-- end learning date -->
        <WeekSelector
          v-model="wpDateSelections.learningDateWeeks"
          data-id-e2e="btnLearningWeeks"
          :model-value="wpDateSelections.learningDateWeeks"
          :week-options="defaultWeekOptions"
          :placeholder="tkey('custom')"
          @update:model-value="val => handleDateUpdate(val, 'learningDateWeeks')"
        />
      </v-card>

      <!-- learning excluding date -->
      <v-card class="pa-2 col-box no-border">
        <div data-id-e2e="dateExclude" class="dates-selection__exclude-container">
          <h3 class="dates-selection__input-heading">{{ tkey('excludingLabel') }}:</h3>
          <MultipleDatesSelector
            :value="wpDateSelections.learningExcludedDates"
            :allowed-dates="allowedStartAndExcludedDates"
            @input="val => handleDateUpdate(val, 'learningExcludedDates')"
          />
        </div>
      </v-card>
      <!-- end excluding date -->
    </template>
    <!-- end learning period -->

    <!-- switching period -->
    <v-card class="mt-5 mb-2 mx-2 col-box border-card">
      <h3 class="dates-selection__input-heading mt-3">
        {{ tkey('switchingPeriodLabel') }}
        <span>{{ tkey('switchingPeriodLabelWeeks') }}</span>

        <!-- Switching period tooltip -->
        <AssortmentTooltip
          :title="$t('tooltips.switchingPeriod.title')"
          :tooltip-sections="switchingPeriodTooltipSections"
        />
      </h3>
      <v-row no-gutters>
        <div class="dates-selection__input-container">
          <div
            data-id-e2e="dateSwitching"
            class="dates-selection__input dates-selection__input--longer"
          >
            <span>{{ tkey('startLabel') }}:</span>
            <RtlsCalendarPicker
              :key="nameKey"
              :value="wpDateSelections.switchingStartDate"
              :allowed-dates="allowedStartAndExcludedDates"
              :display-date-format="longDateFormat"
              @input="val => handleDateUpdate(val, 'switchingStartDate')"
            />
          </div>
          <span class="wp-scope__end-label">
            {{ tkey('endLabel') }}:
            <v-tooltip
              :model-value="endDatesExceeded.switching"
              :disabled="!endDatesExceeded.switching"
              location="right"
            >
              <template #activator="{ props }">
                <b v-bind="props">
                  {{ formatDate(wpDateSelections.switchingEndDate, longDateFormat) }}
                </b>
              </template>
              <span v-if="endDatesExceeded.switching">{{ tkey('maxWeekWarning') }}</span>
            </v-tooltip>
          </span>
        </div>
      </v-row>
      <WeekSelector
        v-model="wpDateSelections.switchingDateWeeks"
        data-id-e2e="btnSwitchingWeeks"
        :model-value="wpDateSelections.switchingDateWeeks"
        :week-options="doubledWeekOptions"
        :placeholder="tkey('custom')"
        @update:model-value="val => handleDateUpdate(val, 'switchingDateWeeks')"
      />
    </v-card>
    <!-- end switching period -->

    <!-- fill items -->
    <v-card class="mt-2 mx-2 col-box border-card pb-2">
      <h3 class="dates-selection__input-heading mt-3">
        {{ tkey('fillLabel') }}

        <!-- Fill according to tooltip -->
        <AssortmentTooltip
          :title="$t('tooltips.fillAccordingTo.title')"
          :tooltip-sections="fillAccordingToTooltipSections"
        />
      </h3>
      <v-select
        :model-value="wpModelSelections.fillInSelection"
        :placeholder="$t('sizeTypes.noOption')"
        :items="sizeSelectItems"
        :style="{ width: '280px', 'max-width': '280px' }"
        :list-props="{ density: 'compact', lines: false, class: 'py-0' }"
        :menu-props="{ location: 'top', origin: 'overlap' }"
        hide-details
        @update:model-value="val => updateModel(val, 'fillInSelection')"
      >
        <template #append>
          <v-icon
            :icon="sizeTypeIconMap[wpModelSelections.fillInSelection] || '$empty-cube'"
            size="30"
          />
        </template>
      </v-select>
    </v-card>
    <!-- end fill items -->

    <!-- max trend -->
    <v-card
      v-if="calculateTrendEnabled"
      class="mt-2 mb-1 mx-2 col-box border-card dates-selection__maximum-trend-container"
    >
      <h3 class="dates-selection__input-heading mt-3">
        {{ tkey('maximumTrendLabel') }} <span>({{ tkey('maximumTrendUnit') }})</span>
      </h3>
      <div class="dates-selection__input">
        <v-text-field
          :model-value="wpModelSelections.maximumTrend"
          :rules="rules"
          @update:model-value="val => updateModel(val, 'maximumTrend')"
        />
      </div>
    </v-card>
    <!-- end max trend -->

    <!-- markdown and waste-->
    <v-card
      v-if="useMarkdownAndWasteToCalculateMarginAndPriceEnabled"
      class="mt-2 mx-2 col-box border-card pb-2"
    >
      <v-switch
        :model-value="wpModelSelections.useMarkdownAndWasteToCalculateMarginAndPrice"
        class="ml-2"
        hide-details
        density="compact"
        color="primary"
        :label="tkey('useMarkdownAndWasteLabel')"
        @update:model-value="
          val => updateModel(val, 'useMarkdownAndWasteToCalculateMarginAndPrice')
        "
      />
    </v-card>
    <!-- end markdown and waste-->

    <!-- filter split workpackages -->
    <v-card
      v-if="isSplitWorkpackagesEnabled && !isTemplate"
      class="mt-5 mb-2 mx-2 col-box border-card filter-split-wp"
    >
      <WpScopeFilters />
    </v-card>
    <!-- end filter split workpackages-->
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'pinia';
import { get, isEqual, size, isEmpty } from 'lodash';

// enums
import SIZE_TYPES from '@enums/size-types';

// stores
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useContextStore } from '@/stores/context';
import { useToolDataStore } from '@/stores/tool-data';

// composables
import { useInternationalization } from '@/composables/use-internationalization';
import { useFeature } from '@/composables/use-feature';
import { useDayjs } from '@/composables/use-dayjs';
import { useInputValidations } from '@/composables/input-validations';
import { useWorkpackageScopeTooltipOptions } from '@/composables/use-tooltip-options';

const maximumTrendLimit = 100;
const defaultWeekOptions = [52, 26, 13, 0];
const doubledWeekOptions = defaultWeekOptions.map(x => x * 2);

export default {
  setup() {
    const { t, tkey } = useInternationalization('workpackagePage.scope');
    const {
      bundlesEnabled,
      hasSeasonalityEnabled,
      hasPeriodIdsEnabled,
      betaHasMandatoryPeriodIdsEnabled,
      calculateTrendEnabled,
      betaAutoGenerateWorkpackagePeriodDatesEnabled,
      isSplitWorkpackagesEnabled,
      useMarkdownAndWasteToCalculateMarginAndPriceEnabled,
    } = useFeature();

    const { toNearestSunday, formatDate, formatUtcDate, getIsoWeekday, checkDateIsSameOrBefore } =
      useDayjs();

    const { required, isNumber, isLessThan, isGreaterOrEqual } = useInputValidations();

    const {
      fillAccordingToTooltipSections,
      performancePeriodTooltipSections,
      targetLaunchDateTooltipSections,
      learningPeriodTooltipSections,
      snapshotDateTooltipSections,
      switchingPeriodTooltipSections,
    } = useWorkpackageScopeTooltipOptions();

    return {
      t,
      tkey,
      // features
      bundlesEnabled,
      hasSeasonalityEnabled,
      hasPeriodIdsEnabled,
      betaHasMandatoryPeriodIdsEnabled,
      calculateTrendEnabled,
      betaAutoGenerateWorkpackagePeriodDatesEnabled,
      useMarkdownAndWasteToCalculateMarginAndPriceEnabled,
      isSplitWorkpackagesEnabled,
      // tooltip sections
      fillAccordingToTooltipSections,
      performancePeriodTooltipSections,
      targetLaunchDateTooltipSections,
      learningPeriodTooltipSections,
      snapshotDateTooltipSections,
      switchingPeriodTooltipSections,
      // date utils
      toNearestSunday,
      formatUtcDate,
      formatDate,
      getIsoWeekday,
      checkDateIsSameOrBefore,
      // validations
      required,
      isNumber,
      isLessThan,
      isGreaterOrEqual,

      defaultWeekOptions,
      doubledWeekOptions,
    };
  },

  data() {
    return {
      nameKey: false,
      datesLoaded: false,
      sizeTypeIconMap: {
        [SIZE_TYPES.linearSpace]: '$linear-space',
        [SIZE_TYPES.cubicSpace]: '$cubic-space',
        [SIZE_TYPES.horizontalSpace]: '$horizontal-space',
        [SIZE_TYPES.frontalSpace]: '$frontal-space',
        [SIZE_TYPES.productCount]: '$empty-cube',
      },
      rules: [
        this.required,
        v => this.isGreaterOrEqual(v, 0),
        v => this.isLessThan(v, maximumTrendLimit),
        this.isNumber,
      ],
    };
  },

  computed: {
    ...mapState(useContextStore, ['clientConfig', 'getDateFormats', 'getClientConfig']),
    ...mapState(useWorkpackagesStore, [
      'isSimpleSwapsWP',
      'loadingAGsForPeriodId',
      'wpDateSelections',
      'wpModelSelections',
      'workpackageCopy',
      'selectedWorkpackage',
      'latestSnapshotDate',
      'snapshotDates',
      'endDatesExceeded',
    ]),
    ...mapState(useToolDataStore, { periodIdsOptions: 'periodIds', periodDates: 'periodDates' }),
    ...mapGetters(useWorkpackagesStore, ['formattedSnapshotDates']),

    parsedPeriodIdsOptions() {
      const periodIds = this.periodIdsOptions.map(el => {
        return {
          text: el,
          value: el,
        };
      });

      if (this.betaHasMandatoryPeriodIdsEnabled) {
        periodIds.unshift({
          text: this.tkey('noPeriodID'),
          value: null,
        });
      }

      return periodIds;
    },

    automaticDatesDisabledTooltip() {
      const noDatesSelectedValue = [null];
      const { periodIds } = this.wpModelSelections;
      if (isEmpty(periodIds) || isEqual(periodIds, noDatesSelectedValue)) {
        return this.tkey('tooltipMustHaveOnePeriod');
      }

      if (!this.selectedPeriodHasSingleDateRange) {
        return this.tkey('tooltipPeriodsMustHaveSameDates');
      }

      return null;
    },

    longDateFormat() {
      return this.getDateFormats.long;
    },

    latestSelectableDate() {
      return this.toNearestSunday(this.latestSnapshotDate);
    },

    autoGenerateWorkpackagePeriodDatesEnabled() {
      return get(this.getClientConfig, 'betaFeatures.autoGenerateWorkpackagePeriodDates', false);
    },

    selectedPeriodHasSingleDateRange() {
      return size(this.periodDateRange) === 2;
    },

    sizeSelectItems() {
      return Object.keys(SIZE_TYPES).map(key => ({
        value: SIZE_TYPES[key],
        title: this.t(`sizeTypes.${key}`),
      }));
    },

    allowedStartAndExcludedDates() {
      // must be Monday and <= latestSelectableDate
      return date =>
        this.getIsoWeekday(date) === 1 &&
        this.checkDateIsSameOrBefore(this.formatUtcDate(date), this.latestSelectableDate, 'day');
    },

    periodDateRange() {
      const { periodIds } = this.wpModelSelections;
      if (isEmpty(periodIds) || isEmpty(this.periodDates)) return [];

      const noPeriodExplicitlySelected = periodIds.includes(null);
      const noPeriodsHaveBeenSelected = !size(periodIds);
      if (noPeriodExplicitlySelected || noPeriodsHaveBeenSelected) return new Set();
      const periods = periodIds.map(periodId => this.periodDates[periodId]);

      const allDatesFound = periods.reduce((allDates, periodObj) => {
        allDates.add(periodObj.effectiveDatePlanned);
        allDates.add(periodObj.expiryDatePlanned);

        return allDates;
      }, new Set());

      return allDatesFound;
    },

    isTemplate() {
      return !!this.selectedWorkpackage.isTemplate;
    },
  },

  watch: {
    longDateFormat() {
      // force rtls-calendar-picker to re-render with new date format
      this.nameKey = !this.nameKey;
    },
  },

  async created() {
    await this.fetchSnapshotDates();
    this.initWpSetupData();
    if (this.hasPeriodIdsEnabled) {
      this.getPeriodIds();
    }
    this.datesLoaded = true;
  },

  methods: {
    ...mapActions(useToolDataStore, ['getPeriodIds']),
    ...mapActions(useWorkpackagesStore, [
      'handleAutoAssortmentGroups',
      'updateWpDateSelections',
      'calculateDatesFromPeriodId',
      'updateWpModelSelections',
      'fetchSnapshotDates',
      'initWpSetupData',
    ]),

    handleDateUpdate(value, field) {
      this.updateWpDateSelections({ field, value });
    },

    updateModel(val, field) {
      this.updateWpModelSelections({ [field]: val });
    },

    async generateDatesFromPeriodId() {
      // only callable if there's a single set of dates
      const [start, end] = this.periodDateRange;
      this.calculateDatesFromPeriodId(start, end);
      this.handleAutoAssortmentGroups();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.autodate-btn {
  height: 3rem !important;
}

.dates-selection {
  &__input-heading {
    margin-bottom: 7px;
    font-size: 1.2rem;

    span {
      font-weight: normal;
    }
  }

  &__input {
    :deep(.v-input__slot) {
      background: #fff;
      border-bottom: 1px solid $assortment-primary-colour !important;
      width: 100px;
    }

    .v-input,
    .v-select {
      margin: 0;
      padding: 0;
      width: 60%;
    }

    &--longer {
      align-items: center;
      display: flex;
      justify-content: center;
      margin-right: 14px;

      .v-input {
        width: 90% !important;
      }
    }

    &--custom {
      height: 20px;
    }
  }

  &__input-container {
    align-items: center;
    display: flex;
    justify-content: flex-start;
    margin-bottom: 14px;

    span {
      font-size: 1.2rem;
      margin-right: 23px;
    }
  }

  &__group-button {
    border-color: $assortment-primary-colour !important;
    height: 30px !important;
    flex: 1;
  }

  &__exclude-container {
    align-items: flex-start;
    display: flex;
    font-weight: normal;
    justify-content: flex-start;

    h3 {
      margin-top: 5px;
      margin-right: 15px;
      font-weight: normal;
    }
  }

  &__maximum-trend-container {
    .rtls-text-field-container {
      width: 100px;
    }
  }
}

.border-card {
  border-top: 1px solid $assortment-divider-colour-darker;
}

.v-card {
  padding-bottom: 0 !important;
}

:deep(.v-text-field__slot input) {
  padding-left: 5px;
}

:deep(.v-item-group) {
  width: 100%;
}

:deep(.v-chip) {
  background: $assortment-workpackage-chip-colour !important;
}

:deep(.v-chip) {
  background: $assortment-workpackage-chip-colour !important;
}

.filter-split-wp {
  padding-top: 20px;
}
</style>
